import { useMemo, useState } from 'react';

/**
 * Client-side pagination for an in-memory list. Returns the current page's
 * slice plus controls. Clamps the page if the list shrinks (e.g. after a
 * delete) so you never land on an empty page past the end.
 */
export function usePagination<T>(items: T[], pageSize = 12) {
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  const current = Math.min(page, pageCount - 1);

  const slice = useMemo(
    () => items.slice(current * pageSize, current * pageSize + pageSize),
    [items, current, pageSize]
  );

  return {
    page: current,
    pageCount,
    slice,
    total: items.length,
    setPage,
    next: () => setPage((p) => Math.min(p + 1, pageCount - 1)),
    prev: () => setPage((p) => Math.max(p - 1, 0)),
  };
}
